import React from 'react';
import Modal from './Modal';
import { useTheme } from '../../../context/ThemeContext';

// Rename Chat Modal
export const RenameChatModal = ({ isOpen, onClose, chatTitle, onRename }) => {
  const { theme } = useTheme();
  const [title, setTitle] = React.useState(chatTitle || '');

  React.useEffect(() => {
    setTitle(chatTitle || '');
  }, [chatTitle]);

  const handleSubmit = () => {
    if (title.trim()) {
      onRename(title.trim());
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Rename Chat"
      footer={
        <>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors font-rasa ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim()}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors font-rasa disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </>
      }
    >
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className={`w-full px-4 py-2 rounded-lg border font-rasa ${
          theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
        placeholder="Enter chat title"
        autoFocus
        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
      />
    </Modal>
  );
};

// Move to Project Modal
export const MoveToProjectModal = ({ isOpen, onClose, projects, currentProjectId, onMove }) => {
  const { theme } = useTheme();
  const [targetProjectId, setTargetProjectId] = React.useState('');

  const handleSubmit = () => {
    if (targetProjectId) {
      onMove(parseInt(targetProjectId));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Move to Project"
      footer={
        <>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors font-rasa ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!targetProjectId}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors font-rasa disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Move
          </button>
        </>
      }
    >
      <label className={`block text-sm font-rasa mb-2 ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
      }`}>
        Select project
      </label>
      <select
        value={targetProjectId}
        onChange={(e) => setTargetProjectId(e.target.value)}
        className={`w-full px-4 py-2 rounded-lg border font-rasa ${
          theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
      >
        <option value="">Choose a project...</option>
        {projects.filter(p => !p.archived && p.id !== currentProjectId).map(p => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
    </Modal>
  );
};

// Add to Project Modal (for chats without project)
export const AddToProjectModal = ({ isOpen, onClose, projects, onAdd }) => {
  const { theme } = useTheme();
  const [targetProjectId, setTargetProjectId] = React.useState('');

  const handleSubmit = () => {
    if (targetProjectId) {
      onAdd(parseInt(targetProjectId));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add to Project"
      footer={
        <>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors font-rasa ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!targetProjectId}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors font-rasa disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </>
      }
    >
      <label className={`block text-sm font-rasa mb-2 ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
      }`}>
        Select project
      </label>
      <select
        value={targetProjectId}
        onChange={(e) => setTargetProjectId(e.target.value)}
        className={`w-full px-4 py-2 rounded-lg border font-rasa ${
          theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
      >
        <option value="">Choose a project...</option>
        {projects.filter(p => !p.archived).map(p => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
    </Modal>
  );
};

// Move or Copy Document to Project Modal
export const DocumentActionModal = ({ isOpen, onClose, projects, currentProjectId, documentName, onMove, onCopy }) => {
  const { theme } = useTheme();
  const [targetProjectId, setTargetProjectId] = React.useState('');
  const [action, setAction] = React.useState('copy');

  React.useEffect(() => {
    if (isOpen) {
      setTargetProjectId('');
      setAction('copy');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!targetProjectId) return;
    const id = parseInt(targetProjectId);
    if (action === 'move') {
      onMove(id);
    } else {
      onCopy(id);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={action === 'move' ? 'Move Document' : 'Copy Document'}
      footer={
        <>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors font-rasa ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!targetProjectId}
            className={`px-4 py-2 rounded-lg text-white transition-colors font-rasa disabled:opacity-50 disabled:cursor-not-allowed ${
              action === 'move'
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {action === 'move' ? 'Move' : 'Copy'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className={`text-sm font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          {documentName}
        </p>

        {/* Action toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setAction('copy')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-rasa transition-colors ${
              action === 'copy'
                ? 'bg-blue-500 text-white'
                : theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Copy
          </button>
          <button
            onClick={() => setAction('move')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-rasa transition-colors ${
              action === 'move'
                ? 'bg-orange-500 text-white'
                : theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Move
          </button>
        </div>

        {/* Project select */}
        <div>
          <label className={`block text-sm font-rasa mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Select project
          </label>
          <select
            value={targetProjectId}
            onChange={(e) => setTargetProjectId(e.target.value)}
            className={`w-full px-4 py-2 rounded-lg border font-rasa ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">Choose a project...</option>
            {projects.filter(p => !p.archived && (action === 'copy' || p.id !== currentProjectId)).map(p => (
              <option key={p.id} value={p.id}>{p.name}{p.id === currentProjectId ? ' (current)' : ''}</option>
            ))}
          </select>
        </div>

        {action === 'move' && (
          <p className={`text-xs font-rasa ${theme === 'dark' ? 'text-orange-400' : 'text-orange-600'}`}>
            The document will be removed from this project.
          </p>
        )}
      </div>
    </Modal>
  );
};

// Remove from Project Confirmation
export const RemoveFromProjectModal = ({ isOpen, onClose, chatTitle, onConfirm }) => {
  const { theme } = useTheme();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Remove from Project"
      footer={
        <>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors font-rasa ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white transition-colors font-rasa"
          >
            Remove
          </button>
        </>
      }
    >
      <p className="font-rasa">
        Are you sure you want to remove "{chatTitle || 'Untitled Chat'}" from this project?
        The chat will still be available in your chat history.
      </p>
    </Modal>
  );
};
